/**
 * b2b-plus-quick-view.js
 *
 * Defines the `<b2b-plus-quick-view>` custom element.
 *
 * Usage in Liquid:
 *   <b2b-plus-quick-view data-stylesheet="...css urls...">
 *     <button type="button" data-quick-view-button data-handle="{{ product.handle }}">
 *       Quick View
 *     </button>
 *   </b2b-plus-quick-view>
 *
 * When the trigger button is clicked the element:
 *   1. Opens the global modal with a loading placeholder.
 *   2. Reads product JSON from `data-b2b-plus-product-json` on the card when present,
 *      else fetches it; then loads inventory and the IndexedDB cart record in parallel.
 *   3. Renders a fully configured `<b2b-plus-product-table>` inside the modal
 *      (quantities reflect the current B2B cart for that product handle).
 *   4. Closes the modal on error.
 */

import { render } from 'lit-html';
import { cartLoadingTemplate } from 'b2b-plus/ui-templates';
import {
  getInventory,
} from 'b2b-plus/inventory';
import {
  openModal,
  closeModal,
  toastNotification,
} from 'b2b-plus/ui';
import {
  getProductRecord,
  getProductJSON,
} from 'b2b-plus/utils';

class B2BPlusQuickView extends HTMLElement {

  // Prevents two opens launching from the same element before the first
  // async open has set triggerButton.disabled (same-microtask double-click).
  #busy = false;

  // ── Lifecycle ────────────────────────────────────────────────────────────

  /**
   * Called by the browser when the element is inserted into the DOM.
   * Attaches a delegated click listener so the handler works even if the
   * trigger button is added to the DOM after `connectedCallback` fires.
   */
  connectedCallback() {
    this.addEventListener('click', (clickEvent) => {
      if (!this.#busy && clickEvent.target.closest('[data-quick-view-button]')) {
        this.#openQuickView();
      }
    });
  }

  // ── Private methods ──────────────────────────────────────────────────────

  /**
   * Opens the Quick View modal for the product associated with the clicked
   * trigger button.
   *
   * Flow:
   *  1. Reads the product `handle` from `data-handle` on the trigger button.
   *  2. Shows a loading placeholder in the global modal immediately.
   *  3. Uses `script[data-b2b-plus-product-json="<product id>"]` from anywhere in
   *     the document when present; otherwise fetches product JSON via
   *     `getProductJSON`. Then loads inventory and the v2 cart record for the handle.
   *  4. Creates a `<b2b-plus-product-table>` with pre-injected data and
   *     replaces the loading placeholder.
   *
   * @returns {Promise<void>}
   */
  async #openQuickView() {
    this.#busy = true;
    const { strings: b2bStrings } = window.b2bPlus;

    const triggerButton = this.querySelector('[data-quick-view-button]');
    const productHandle = triggerButton?.dataset.handle;
    if (!productHandle) { this.#busy = false; return; }

    // Show a loading placeholder immediately so the modal opens without delay
    const loadingPlaceholder = document.createElement('div');
    render(cartLoadingTemplate(), loadingPlaceholder);
    openModal({ title: '', content: loadingPlaceholder, className: 'b2b-plus-quick-view-modal' });

    // Prevent double-open while the async work is in flight
    if (triggerButton) triggerButton.disabled = true;

    try {
      // ── 1. Product JSON: embedded on card, or catalog-price API fallback ─
      const productId      = triggerButton?.dataset.productId ?? '';
      // The JSON script is keyed by product id, so look it up directly — card
      // markup nests the trigger differently per template, and the id is unique.
      const embeddedScript = productId
        ? document.querySelector(`script[data-b2b-plus-product-json="${CSS.escape(productId)}"]`)
        : null;
      let   productJSON    = embeddedScript ? B2BPlusQuickView._parseEmbeddedProductJson(embeddedScript, productHandle) : null;
      if (!productJSON) {
        // The catalog-price API resolves by product id, so the trigger carries one.
        if (!productId) throw new Error('b2b-plus: quick view needs a product id for ' + productHandle);
        productJSON = await getProductJSON({ handle: productHandle, productId });
      }
      if (!productJSON?.handle) throw new Error('b2b-plus: product not found for handle ' + productHandle);

      // ── 2. Fetch inventory and IndexedDB cart record (nested v2 shape — avoids a second IDB read in the table)
      const [inventoryResponse, cartRecord] = await Promise.all([
        getInventory([productJSON.id].filter(Boolean)),
        getProductRecord(productHandle),
      ]);

      const inventoryData = inventoryResponse?.success && inventoryResponse?.data
        ? inventoryResponse.data
        : null;

      // Ensure the table module finished loading (separate script); otherwise the
      // host shows an empty node and appears "stuck" on the loading placeholder.
      if (!customElements.get('b2b-plus-product-table')) {
        await Promise.race([
          customElements.whenDefined('b2b-plus-product-table'),
          new Promise((_, reject) => {
            setTimeout(() => reject(new Error('b2b-plus-product-table module did not load in time')), 8000);
          }),
        ]);
      }

      // ── 3. Build the product table ───────────────────────────────────────
      const productTableElement = document.createElement('b2b-plus-product-table');
      productTableElement.dataset.showProductDetails  = 'true';
      productTableElement.dataset.showInventoryFilter = 'true';
      productTableElement.dataset.showActionButton    = 'true';

      // Forward any stylesheet URLs declared on this element
      if (this.dataset.stylesheet) {
        productTableElement.dataset.stylesheet = this.dataset.stylesheet;
      }

      // Product table shows success toast by default (`data-toast-on-order` not false);
      // close the modal when order is saved (see b2b-plus-product-table #initAtcButton).
      productTableElement.dataset.closeModalOnOrder = 'true';
      productTableElement.addEventListener('b2b:order-committed', () => {
        closeModal();
      }, { once: true });

      // Pre-inject fetched data BEFORE inserting the element into the DOM.
      // connectedCallback skips its setTimeout(0) yield when prefetched data
      // is already set, so this call MUST happen before openModal() /
      // any container.prepend() call that triggers connectedCallback.
      productTableElement.setPrefetchedData({
        productJSON,
        inventoryData,
        cartRecord,
      });

      // Embed the product JSON as a script tag for the table's internal parser
      const productJsonScriptTag = document.createElement('script');
      productJsonScriptTag.type  = 'application/json';
      productJsonScriptTag.setAttribute('data-product-json', '');
      productJsonScriptTag.textContent = JSON.stringify(productJSON);
      productTableElement.appendChild(productJsonScriptTag);

      // ── 4. Replace loading placeholder with the populated table ──────────
      openModal({ title: 'Quick Add', content: productTableElement, className: 'b2b-plus-quick-view-modal' });

    } catch (error) {
      console.error('b2b-plus: quick-view failed', error);
      closeModal();
      toastNotification(b2bStrings.quickAddError, 'error');

    } finally {
      this.#busy = false;
      if (triggerButton) triggerButton.disabled = false;
    }
  }

  /**
   * @param {HTMLScriptElement} scriptEl
   * @param {string} handleFallback
   * @returns {object|null}
   */
  static _parseEmbeddedProductJson(scriptEl, handleFallback) {
    if (!scriptEl?.textContent?.trim()) return null;
    let raw;
    try {
      raw = JSON.parse(scriptEl.textContent);
    } catch {
      return null;
    }
    if (!raw || typeof raw !== 'object') return null;

    const out = { ...raw };
    if (out.handle == null && handleFallback) out.handle = handleFallback;
    if (out.description == null && out.body_html != null) out.description = out.body_html;
    if (Array.isArray(out.images)) {
      out.images = out.images
        .map((img) => (typeof img === 'string' ? img : img?.src ?? ''))
        .filter(Boolean);
    }
    return out;
  }
}

customElements.define('b2b-plus-quick-view', B2BPlusQuickView);
